import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable, inject } from '@angular/core';
import { EnumLabelService } from '../enum-label/enum-label.service';
import { Observable, map, of, tap } from 'rxjs';
import { CountryAndNorthAmericanJurisdictionInfo, CountryInfo } from '../../interfaces/jurisdictions/country-info';
import { JurisdictionInfo } from '../../interfaces/jurisdictions/jurisdiction-info';
import { OrderTypeJurisdictions } from '../../interfaces/jurisdictions/order-type-jurisdictions';
import { FeeType } from '../../interfaces/enum-types/fee-type';
import { OrderStatusType } from '../../interfaces/enum-types/order-status-type';
import { OrderType } from '../../interfaces/enum-types/order-type';
import { SelectOption } from '../../interfaces/select-option';
import { DocumentType } from '../../interfaces/enum-types/document-type';
import { ResultState } from '../../interfaces/enum-types/result-state';
import { LogSeverity } from '../../interfaces/enum-types/log-severity';
import { eAlertType, eAlertTypeGroup, eGeneration, eOrderType, eOrderTypeGroup, eQCFormType } from '../../enums';
import { AssetType } from '../../interfaces/enum-types/assetType';
import { BillingCycle } from '../../interfaces/enum-types/billing-cycle';
import { SystemEventType } from '../../interfaces/enum-types/system-event-type';
import { PartyType } from '../../interfaces/enum-types/party-type';
import { QCFormType } from '../../interfaces/enum-types/qc-form-type';
import { DatabaseType } from '../../interfaces/enum-types/database-type';
import { Generation } from '../../interfaces/generation/generation';
import { REG_HUB_COMMON_CONFIG, RegHubCommonConfig } from '../../reg-hub-common-config';
import { RegistryForm } from '../../interfaces/registry-form';
import { ValidationLogType } from '../../interfaces/enum-types/validation-log-type';
import { AuditState } from '../../interfaces/enum-types/audit-state';
import { BatchType } from '../../interfaces/enum-types/batch-type';
import { BatchState } from '../../interfaces/batch-state/batch-state';
import { AccessLevel } from '../../interfaces/enum-types/access-level';
import { BusinessSearchScope } from '../../interfaces/business-search-criteria/business-search-scope';
import { Partnership } from '../../interfaces/enum-types/partnership';
import { ReportColumn } from '../../interfaces/enum-types/report-column';
import { OrderTypeCountriesAndJurisdictions } from '../../interfaces/jurisdictions/order-type-countries-and-jurisdictions';
import { AlertType } from '../../interfaces/enum-types/alert-type';

@Injectable({
  providedIn: 'root'
})
export class EnumeratedTypesRepositoryService {
  private readonly enumLabels = inject(EnumLabelService);

  private countryInfos: CountryInfo[] | null = null;
  private assetTypes: AssetType[] | null = null;

  constructor(
    private http: HttpClient,
    @Inject(REG_HUB_COMMON_CONFIG) private config: RegHubCommonConfig) { }

  /**
   * Select option whose label is looked up on every read, so dropdowns follow the active language
   * (falls back to the API name when no enums.<enumName> translation exists).
   */
  private enumOption(enumName: string, value: any, name: string): SelectOption {
    const enumLabels = this.enumLabels;
    return {
      value,
      get label(): string { return enumLabels.label(enumName, value, name); }
    } as SelectOption;
  }


  public getOrderTypes(baseUrl: string, orderTypeGroups?: eOrderTypeGroup[], defaultParams?: HttpParams): Observable<OrderType[]> {
    var requestUrl = `${baseUrl}/OrderTypes`;

    if (orderTypeGroups) {
      if (!defaultParams) {
        defaultParams = new HttpParams();
      }

      orderTypeGroups.forEach(id => {
        defaultParams = defaultParams!.append('OrderTypeGroupIDs', id.toString());
      });
    }

    return this.http.get<OrderType[]>(requestUrl, { params: defaultParams });
  }

  public getOrderTypesAsSelectOptions(baseUrl: string, orderTypeGroups?: eOrderTypeGroup[], exclude?: eOrderType[]): Observable<SelectOption[]> {
    let params = new HttpParams()
                      .set('PageSize', 100)
                      .set('SortDirection', "ASC")
                      .set('SortColumn', 'SortIndex');   
    
    if(exclude) {
      exclude.forEach(val => params = params.append('exclude', val));
    }
    
    return this.getOrderTypes(baseUrl, orderTypeGroups, params).pipe(map(types => types.map(this.mapOrderTypeToSelectOption)));
  }

  private mapOrderTypeToSelectOption = (type: OrderType): SelectOption =>
    this.enumOption('orderType', type.id, type.name);

  public getOrderStatusTypes(baseUrl: string, defaultParams?: HttpParams): Observable<OrderStatusType[]> {
    return this.http.get<OrderStatusType[]>(`${baseUrl}/OrderStatusTypes`, { params: defaultParams });
  }

  public getOrderStatusTypesAsSelectOptions(baseUrl: string): Observable<SelectOption[]> {
    let params = new HttpParams().set('PageSize', 100);  
    return this.getOrderStatusTypes(baseUrl, params).pipe(map(types => types.map(this.mapOrderStatusTypeToSelectOption)));
  }

  private mapOrderStatusTypeToSelectOption = (type: OrderStatusType): SelectOption =>
    this.enumOption('orderStatusType', type.id, type.name);

  public getCountryInfo(baseUrl?: string): Observable<CountryInfo[]> {
    if (!this.countryInfos) {
      return this.http.get<CountryInfo[]>(`${baseUrl ?? this.config.rootUrl}/Jurisdictions`)
        .pipe(tap(countryInfos => this.countryInfos = countryInfos));
    } else {
      return of(this.countryInfos);
    }
  }

  public getCountryAndNorthAmericanJurisdictionInfo(): Observable<CountryAndNorthAmericanJurisdictionInfo> {
    return this.getCountryInfo().pipe(map(info => {
      const result: CountryAndNorthAmericanJurisdictionInfo = {
        countries: info.map(i => { return { label: i.countryFullName, value: i.countryCode } as SelectOption }),
        canadianJurisdictions: info.find(i => i.countryCode === 'CA')?.jurisdictions.map(j => { return { label: j.jurisdictionFullName, value: j.jurisdictionCode } as SelectOption })!,
        americanJurisdictions: info.find(i => i.countryCode === 'US')?.jurisdictions.map(j => { return { label: j.jurisdictionFullName, value: j.jurisdictionCode } as SelectOption })!
      };

      return result;
    }));
  }

  public getCountryInfoAsSelectOptions(baseUrl: string): Observable<SelectOption[]> {
    return this.getCountryInfo(baseUrl).pipe(map(infos => infos.map(info => ({
      label: info.countryFullName,
      value: info.countryCode
    }))));
  }
  
  public getCountriesAsSelectOptions(): Observable<SelectOption[]> {
    return this.getCountryInfo(this.config.rootUrl).pipe(map(infos => infos.map(info => ({
      label: info.countryFullName,
      value: info.countryCode
    }))));
  }

  public getJurisdictionsForCountry(country: string): Observable<SelectOption[]> {
    let params = new HttpParams().set('countryCode', country);

    return this.http.get<CountryInfo[]>(`${this.config.rootUrl}/Jurisdictions`, { params: params }).pipe(
      map(infos => infos.flatMap(info => info.jurisdictions.map(jinfo => ({
        label: jinfo.jurisdictionFullName,
        value: jinfo.jurisdictionCode
      })))));
  }

  public getJurisdictionInfo(baseUrl: string, country?: string): Observable<JurisdictionInfo[]> {
    return this.filterAndMapJurisdictions(this.getCountryInfo(baseUrl), country);
  }

  public getJurisdictionInfoAsSelectOptions(baseUrl: string, country: string): Observable<SelectOption[]> {
    return this.getJurisdictionInfo(baseUrl, country).pipe(map(infos => infos.map(info => ({
      label: info.jurisdictionFullName,
      value: info.jurisdictionCode
    }))));
  }

  public getOrderTypeJurisdictions(orderTypes?: eOrderType[]): Observable<OrderTypeJurisdictions[]> {
    let params = new HttpParams();
    if (orderTypes) {
      orderTypes.forEach(id => {
        params = params.append('OrderTypeIDs', id.toString());
      });
    }

    return this.http.get<OrderTypeJurisdictions[]>(`${this.config.rootUrl}/Jurisdictions/Orders`, { params });
  }

  public getOrderTypeCountriesAndJurisdictions(orderTypes?: eOrderType[]): Observable<OrderTypeCountriesAndJurisdictions[]> {
    let params = new HttpParams();
    if (orderTypes) {
      orderTypes.forEach(id => {
        params = params.append('OrderTypeIDs', id.toString());
      });
    }

    return this.http.get<OrderTypeCountriesAndJurisdictions[]>(`${this.config.rootUrl}/Jurisdictions/Countries`, { params });
  }

  public getAssetTypes(orderTypeID: eOrderType, jurisdiction: string, historical: boolean = false): Observable<AssetType[]> {
    let params = new HttpParams();

    if (historical) {
      params = params.set('historical', historical);
    }

    return this.http.get<AssetType[]>(`${this.config.rootUrl}/AssetTypes/${orderTypeID}/${jurisdiction}`, { params: params })
      .pipe(tap(assetTypes => this.assetTypes = assetTypes));
  }

  public getAssetTypesAsSelectOptions(orderTypeID: eOrderType, jurisdiction: string, historical: boolean = false): Observable<SelectOption[]> {
    return this.getAssetTypes(orderTypeID, jurisdiction, historical).pipe(map(infos => infos.map(info =>
      this.enumOption('assetType', info.id, info.name))));
  }

  private filterAndMapJurisdictions(countryInfos: Observable<CountryInfo[]>, country?: string): Observable<JurisdictionInfo[]> {
    return countryInfos.pipe(
      map(countryInfos => countryInfos.filter(info => country ? info.countryCode === country : false)),
      map(countryInfos => countryInfos.flatMap(info => info.jurisdictions)))
  }

  public getFeeTypes(baseUrl: string): Observable<FeeType[]> {
    return this.http.get<FeeType[]>(`${baseUrl}/FeeTypes`);
  }

  public getFeeTypesAsSelectOptions(baseUrl: string): Observable<SelectOption[]> {
    return this.getFeeTypes(baseUrl).pipe(map(types => types.map(this.mapFeeTypeToSelectOption)));
  }

  private mapFeeTypeToSelectOption = (type: FeeType): SelectOption =>
    this.enumOption('feeType', type.id, type.name);

  public getDocumentTypes(baseUrl: string): Observable<DocumentType[]> {
    return this.http.get<DocumentType[]>(`${baseUrl}/DocumentTypes`);
  }

  public getDocumentTypesAsSelectOptions(baseUrl: string): Observable<SelectOption[]> {
    return this.getDocumentTypes(baseUrl).pipe(map(types => types.map(this.mapDocumentTypeToSelectOption)));
  }

  private mapDocumentTypeToSelectOption(type: DocumentType): SelectOption {
    return { label: type.name, value: type.id };
  }

  public getResultStates(baseUrl: string): Observable<ResultState[]> {
    return this.http.get<ResultState[]>(`${baseUrl}/ResultStates`);
  }

  public getResultStatesAsSelectOptions(baseUrl: string): Observable<SelectOption[]> {
    return this.getResultStates(baseUrl).pipe(map(types => types.map(this.mapResultStateToSelectOption)));
  }

  private mapResultStateToSelectOption = (type: ResultState): SelectOption =>
    this.enumOption('resultState', type.id, type.name);

  public getLogSeverities(baseUrl: string): Observable<ResultState[]> {
    return this.http.get<LogSeverity[]>(`${baseUrl}/LogSeverities`);
  }

  public getLogSeveritiesAsSelectOptions(baseUrl: string): Observable<SelectOption[]> {
    return this.getLogSeverities(baseUrl).pipe(map(types => types.map(this.mapLogSeveritiesToSelectOption)));
  }

  private mapLogSeveritiesToSelectOption = (type: ResultState): SelectOption =>
    this.enumOption('logSeverity', type.id, type.name);

  public getBillingCycles(baseUrl: string): Observable<BillingCycle[]> {
    return this.http.get<BillingCycle[]>(`${baseUrl}/BillingCycles`);
  }

  public getBillingCyclesAsSelectOptions(baseUrl: string): Observable<SelectOption[]> {
    return this.getBillingCycles(baseUrl).pipe(map(types => types.map(this.mapBillingCyclesToSelectOption)));
  }

  private mapBillingCyclesToSelectOption(type: BillingCycle): SelectOption {
    return { label: type.name, value: type.id };
  }

  public getSystemEventTypes(baseUrl: string): Observable<SystemEventType[]> {
    return this.http.get<SystemEventType[]>(`${baseUrl}/SystemEventTypes`);
  }

  public getSystemEventTypesAsSelectOptions(baseUrl: string): Observable<SelectOption[]> {
    return this.getSystemEventTypes(baseUrl).pipe(map(types => types.map(this.mapSystemEventTypeToSelectOption)));
  }

  private mapSystemEventTypeToSelectOption = (type: SystemEventType): SelectOption =>
    this.enumOption('systemEventType', type.id, type.name);

  public getPartyTypes(baseUrl: string): Observable<PartyType[]> {
    return this.http.get<PartyType[]>(`${baseUrl}/PartyTypes`);
  }

  public getPartyTypesAsSelectOptions(baseUrl: string): Observable<SelectOption[]> {
    return this.getPartyTypes(baseUrl).pipe(map(types => types.map(this.mapPartyTypeToSelectOption)));
  }

  private mapPartyTypeToSelectOption = (type: PartyType): SelectOption =>
    this.enumOption('partyType', type.id, type.name);

  public getQcFormTypes(baseUrl: string, formIds?: eQCFormType[]): Observable<QCFormType[]> {
    let params = new HttpParams();
    if (formIds) {
      formIds.forEach(id => {
        params = params.append('QCFormType', id.toString());
      });
    }

    return this.http.get<RegistryForm[]>(`${baseUrl}/RegistryForms`, { params })
      .pipe(
        map(registryForms => registryForms.map(form => {
          const splitForm = form.formFull.split('(');
          const formName = splitForm.at(0) ? form.formAbbrev + " - " + splitForm.at(0)?.trim() : form.formAbbrev;

          return {
            id: form.formAbbrev,
            name: formName
          } as QCFormType;
        })));
  }

  public getQcFormTypesAsSelectOptions(baseUrl: string, formIds?: eQCFormType[]): Observable<SelectOption[]> {
    return this.getQcFormTypes(baseUrl, formIds).pipe(map(infos => infos.map(info => ({
      label: info.name,
      value: info.id
    }))));
  }

  public getQcFormTypesAsSelectOptionsCompact(baseUrl: string, formIds?: eQCFormType[]): Observable<SelectOption[]> {
    return this.getQcFormTypes(baseUrl, formIds).pipe(
      map(infos => infos.map(info => ({
        label: info.name.split(" ")[0],
        value: info.id
      }))),
      map(infos => infos.sort((a, b) =>
        a.value.toString().localeCompare(b.value.toString(), undefined, { sensitivity: 'base' })
      )));
  }

  public getDatabaseTypes(baseUrl: string): Observable<DatabaseType[]> {
    return this.http.get<DatabaseType[]>(`${baseUrl}/DataBaseTypes`);
  }

  public getDatabaseTypesAsSelectOptions(baseUrl: string): Observable<SelectOption[]> {
    return this.getDatabaseTypes(baseUrl).pipe(map(types => types.map(this.mapDatabaseTypeToSelectOption)));
  }

  private mapDatabaseTypeToSelectOption = (type: DatabaseType): SelectOption =>
    this.enumOption('databaseType', type.id, type.name);

  public getGenerations(): Observable<SelectOption[]> {
    return this.http.get<Generation[]>(`${this.config.rootUrl}/Generations`)
      .pipe(map(generations => generations.map(gen => ({ 
          label: gen.name, 
          value: gen.id }))));
  }

  public getValidationLogTypes(baseUrl: string, defaultParams?: HttpParams): Observable<ValidationLogType[]> {
    return this.http.get<ValidationLogType[]>(`${baseUrl}/ValidationLogTypes`, { params: defaultParams });
  }

  public getValidationLogTypesAsSelectOptions(baseUrl: string): Observable<SelectOption[]> {
    let params = new HttpParams().set('PageSize', 100);  
    return this.getValidationLogTypes(baseUrl, params).pipe(map(types => types.map(this.mapValidationLogTypesToSelectOption)));
  }

  private mapValidationLogTypesToSelectOption = (type: ValidationLogType): SelectOption =>
    this.enumOption('validationLogType', type.id, type.name);

  public getAuditStates(baseUrl: string, defaultParams?: HttpParams): Observable<AuditState[]> {
    return this.http.get<AuditState[]>(`${baseUrl}/AuditStates`, { params: defaultParams });
  }

  public getAuditStatesAsSelectOptions(baseUrl: string): Observable<SelectOption[]> {
    let params = new HttpParams().set('PageSize', 100);  
    return this.getAuditStates(baseUrl, params).pipe(map(types => types.map(this.mapAuditStatesToSelectOption)));
  }

  private mapAuditStatesToSelectOption = (type: AuditState): SelectOption =>
    this.enumOption('auditState', type.id, type.name);

  public getAccessLevels(baseUrl: string, defaultParams?: HttpParams): Observable<AccessLevel[]> {
    return this.http.get<AccessLevel[]>(`${baseUrl}/AccessLevels`, { params: defaultParams });
  }

  public getAccessLevelsAsSelectOptions(baseUrl: string): Observable<SelectOption[]> {
    let params = new HttpParams().set('PageSize', 100);  
    return this.getAccessLevels(baseUrl, params).pipe(map(types => types.map(this.mapAccessLevelsToSelectOption)));
  }

  private mapAccessLevelsToSelectOption = (type: AccessLevel): SelectOption =>
    this.enumOption('accessLevel', type.id, type.name);

  public getBatchTypes(batchesURL: string, defaultParams?: HttpParams): Observable<BatchType[]> {
    return this.http.get<BatchType[]>(`${batchesURL}/BatchTypes`, { params: defaultParams });
  }

  public getBatchTypesAsSelectOptions(batchesURL: string): Observable<SelectOption[]> {
    let params = new HttpParams().set('PageSize', 100);  
    return this.getBatchTypes(batchesURL, params).pipe(map(types => types.map(this.mapBatchTypesToSelectOption)));
  }

  private mapBatchTypesToSelectOption = (type: BatchType): SelectOption =>
    this.enumOption('batchType', type.id, type.name);

  public getBatchStates(batchesURL: string, defaultParams?: HttpParams): Observable<BatchState[]> {
    return this.http.get<BatchState[]>(`${batchesURL}/BatchStates`, { params: defaultParams });
  }

  public getBatchStatesAsSelectOptions(batchesURL: string): Observable<SelectOption[]> {
    let params = new HttpParams().set('PageSize', 100);  
    return this.getBatchStates(batchesURL, params).pipe(map(types => types.map(this.mapBatchStatesToSelectOption)));
  }

  private mapBatchStatesToSelectOption = (type: BatchState): SelectOption =>
    this.enumOption('batchState', type.id, type.name);
  
  public getBusinessSearchScopesAsSelectOptions(): Observable<BusinessSearchScope[]> {
    return this.http.get<BusinessSearchScope[]>(`${this.config.rootUrl}/BusinessSearchScopes`);
  }

  public getPartnerships(baseUrl: string): Observable<Partnership[]> {
    return this.http.get<Partnership[]>(`${baseUrl}/Partnerships`);
  }

  public getReportColumns(baseUrl: string): Observable<ReportColumn[]> {
    return this.http.get<ReportColumn[]>(`${baseUrl}/ReportColumns`);
  }

  public getAlertTypes(baseUrl: string, alertTypeGroups?: eAlertTypeGroup[], defaultParams?: HttpParams): Observable<AlertType[]> {
    var requestUrl = `${baseUrl}/AlertTypes`;

    if (alertTypeGroups) {
      if (!defaultParams) {
        defaultParams = new HttpParams();
      }

      alertTypeGroups.forEach(id => {
        defaultParams = defaultParams!.append('AlertTypeGroupIDs', id.toString());
      });
    }

    // The API pages alert types like any list (default 10), but every screen needs all of them
    if (!defaultParams) {
      defaultParams = new HttpParams();
    }

    if (!defaultParams.has('pageSize')) {
      defaultParams = defaultParams.set('pageSize', 1000);
    }

    return this.http.get<AlertType[]>(requestUrl, { params: defaultParams });
  }

  public getAlertTypesAsSelectOptions(baseUrl: string, alertTypeGroups?: eAlertTypeGroup[], exclude?: eAlertType[]): Observable<SelectOption[]> {
    let params = new HttpParams()
                      .set('PageSize', 100)
                      .set('SortDirection', "ASC")
                      .set('SortColumn', 'SortIndex');   
    
    if(exclude) {
      exclude.forEach(val => params = params.append('exclude', val));
    }
    
    return this.getAlertTypes(baseUrl, alertTypeGroups, params).pipe(map(types => types.map(this.mapAlertTypeToSelectOption)));
  }

  private mapAlertTypeToSelectOption = (type: AlertType): SelectOption =>
    this.enumOption('alertType', type.id, type.name);
}
